import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Bug, Lightbulb, Camera, X, Video, Square } from "lucide-react";
import { fileToCompressedDataUrl } from "@/lib/imageCompress";
import { getApiLog } from "@/lib/apiLog";

export const FEEDBACK_STATUS_META = {
  pending: { label: "Pending review", cls: "bg-amber-100 text-amber-800" },
  in_progress: { label: "In progress", cls: "bg-sky-100 text-sky-800" },
  fixed: { label: "Fixed", cls: "bg-emerald-100 text-emerald-800" },
  declined: { label: "Declined", cls: "bg-secondary text-muted-foreground" },
};

export const FeedbackStatusChip = ({ status }) => {
  const m = FEEDBACK_STATUS_META[status] || FEEDBACK_STATUS_META.pending;
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold uppercase tracking-wider ${m.cls}`}>{m.label}</span>;
};

// Shared "Report issue / Suggest upgrade" dialog for every portal.
// `client` = axios instance, `base` = "/feedback" or "/colony/feedback",
// `T` = optional translate fn (worker portal passes its i18n t).
export const FeedbackDialog = ({ open, onOpenChange, client, base = "/feedback", T = (s) => s }) => {
  const [type, setType] = useState("bug");
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [images, setImages] = useState([]);
  const [mine, setMine] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = React.useRef(null);

  // Screen recording (desktop browsers only — phones don't support it).
  const canRecord = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getDisplayMedia;
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [videoBlob, setVideoBlob] = useState(null);
  const recRef = React.useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ? "video/webm;codecs=vp9" : "video/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 1_200_000 });
      const parts = [];
      rec.ondataavailable = (e) => { if (e.data.size) parts.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(recRef.current?.timer);
        setRecording(false);
        setVideoBlob(new Blob(parts, { type: "video/webm" }));
      };
      stream.getVideoTracks()[0].onended = () => { if (rec.state !== "inactive") rec.stop(); };
      rec.start(1000);
      const timer = setInterval(() => setRecSecs((s) => {
        if (s + 1 >= 90) { if (rec.state !== "inactive") rec.stop(); }
        return s + 1;
      }), 1000);
      recRef.current = { rec, timer };
      setRecSecs(0);
      setRecording(true);
      toast.info(T("Recording… reproduce the problem, then press Stop (max 90s)."));
    } catch { /* user cancelled the share picker */ }
  };
  const stopRecording = () => { const r = recRef.current?.rec; if (r && r.state !== "inactive") r.stop(); };

  const uploadVideo = async () => {
    if (!videoBlob) return "";
    const buf = new Uint8Array(await videoBlob.arrayBuffer());
    const { data: start } = await client.post("/feedback/video/start");
    const CHUNK = 1_500_000;
    for (let i = 0, idx = 0; i < buf.length; i += CHUNK, idx += 1) {
      const slice = buf.subarray(i, i + CHUNK);
      let bin = "";
      for (let j = 0; j < slice.length; j += 0x8000) bin += String.fromCharCode.apply(null, slice.subarray(j, j + 0x8000));
      await client.post("/feedback/video/chunk", { upload_id: start.upload_id, index: idx, data: btoa(bin) });
    }
    const { data: fin } = await client.post("/feedback/video/finish", { upload_id: start.upload_id });
    return fin.video_id;
  };

  useEffect(() => {
    if (!open) return;
    setType("bug"); setTitle(""); setDetails(""); setImages([]); setVideoBlob(null); setRecSecs(0);
    client.get(`${base}/mine`).then((r) => setMine(r.data?.items || [])).catch(() => {});
  }, [open, client, base]);

  const addImages = async (files) => {
    const picked = Array.from(files || []).slice(0, 5 - images.length);
    if (!picked.length) return;
    try {
      const urls = await Promise.all(picked.map((f) => fileToCompressedDataUrl(f)));
      setImages((prev) => [...prev, ...urls].slice(0, 5));
    } catch { toast.error(T("Couldn't read that image.")); }
  };

  const submit = async () => {
    if (!title.trim()) return toast.error(T("Give it a short title first."));
    setSaving(true);
    try {
      const video_id = await uploadVideo();
      await client.post(base, {
        type,
        title: title.trim(),
        details: details.trim(),
        images,
        video_id,
        logs: type === "bug" ? getApiLog() : "",
      });
      toast.success(type === "bug" ? T("Bug reported — thank you! We'll look into it.") : T("Suggestion sent — thank you!"));
      setTitle(""); setDetails(""); setImages([]); setVideoBlob(null);
      const r = await client.get(`${base}/mine`);
      setMine(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto theme-emerald" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle>{T("Report an issue / Suggest an upgrade")}</DialogTitle>
          <DialogDescription>{T("Found a bug or have an idea? Send it straight to the office.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {[
              { k: "bug", label: T("Bug / problem"), icon: Bug },
              { k: "upgrade", label: T("Upgrade request"), icon: Lightbulb },
            ].map(({ k, label, icon: Icon }) => (
              <button
                key={k}
                type="button"
                onClick={() => setType(k)}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold rounded-sm border transition ${type === k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
                data-testid={`feedback-type-${k}`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
          <div>
            <Label>{T("Title")}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={type === "bug" ? T("e.g. Photos won't upload on my phone") : T("e.g. Add a dark mode")} data-testid="feedback-title" />
          </div>
          <div>
            <Label>{T("Details (optional)")}</Label>
            <Textarea rows={3} value={details} onChange={(e) => setDetails(e.target.value)} placeholder={T("What happened? What would you like?")} data-testid="feedback-details" />
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addImages(e.target.files); e.target.value = ""; }} data-testid="feedback-image-input" />
            <div className="flex items-center gap-2 flex-wrap">
              <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => fileRef.current?.click()} disabled={images.length >= 5} data-testid="feedback-add-screenshot">
                <Camera className="w-3.5 h-3.5 mr-1.5" /> {T("Add screenshot")}{images.length ? ` (${images.length}/5)` : ""}
              </Button>
              {canRecord && !videoBlob && (
                recording ? (
                  <Button type="button" variant="destructive" size="sm" className="rounded-sm" onClick={stopRecording} data-testid="feedback-stop-recording">
                    <Square className="w-3.5 h-3.5 mr-1.5" /> {T("Stop")} ({recSecs}s)
                  </Button>
                ) : (
                  <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={startRecording} data-testid="feedback-record-screen">
                    <Video className="w-3.5 h-3.5 mr-1.5" /> {T("Record screen")}
                  </Button>
                )
              )}
              {videoBlob && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-300 rounded-sm px-2 py-1.5" data-testid="feedback-video-attached">
                  <Video className="w-3.5 h-3.5" /> {T("Recording attached")} ({Math.round(videoBlob.size / 1024 / 102.4) / 10}MB)
                  <button type="button" onClick={() => setVideoBlob(null)} data-testid="feedback-video-remove"><X className="w-3.5 h-3.5" /></button>
                </span>
              )}
              {images.map((src, i) => (
                <div key={i} className="relative">
                  <img src={src} alt={`screenshot ${i + 1}`} className="w-12 h-12 object-cover rounded-sm border border-border" data-testid={`feedback-image-thumb-${i}`} />
                  <button type="button" onClick={() => setImages(images.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-destructive text-white rounded-full w-4 h-4 flex items-center justify-center" data-testid={`feedback-image-remove-${i}`}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <Button onClick={submit} disabled={saving || !title.trim()} className="w-full rounded-sm" data-testid="feedback-submit">
            {saving ? T("Sending…") : T("Send it")}
          </Button>

          {mine.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{T("My reports")}</p>
              <div className="border border-border rounded-sm divide-y max-h-48 overflow-y-auto">
                {mine.map((f) => (
                  <div key={f.id} className="px-3 py-2 flex items-center justify-between gap-2 text-sm" data-testid={`feedback-mine-${f.id}`}>
                    <div className="truncate flex items-center gap-1.5">
                      {f.type === "bug" ? <Bug className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" /> : <Lightbulb className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                      <span className="truncate">{f.title}</span>
                    </div>
                    <FeedbackStatusChip status={f.status} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">{T("Close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
